<template>
  <ul :class="$style.squads">
    <li v-for="index in count" :key="index" :class="$style.squad">
      <div :class="$style.info">
        <div :class="$style.avatar" />
        <div :class="$style.desc">
          <div :class="$style.head">
            <div :class="$style.tag" />
            <div :class="$style.points" />
          </div>
          <div :class="$style.name" />
          <div :class="$style.meta" />
        </div>
      </div>

      <div :class="$style.members">
        <div
          v-for="memberIndex in 4"
          :key="memberIndex"
          :class="$style.memberAvatar"
        />
        <div :class="$style.slots" />
      </div>

      <div :class="$style.cover" />
    </li>
  </ul>
</template>

<script setup lang="ts">
import { computed } from 'vue'

defineOptions({
  name: 'UiSkeletonSquadsList',
  inheritAttrs: false
})

const props = withDefaults(
  defineProps<{
    count?: number
  }>(),
  {
    count: 4
  }
)

const count = computed(() => (props.count && props.count > 0 ? props.count : 4))
</script>

<style module lang="scss">
@keyframes shimmer {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
}

.skeletonItem {
  background: var-default(--skeleton-item-bg);
  border-radius: 0.8em;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    transform: translateX(-100%);
    background: linear-gradient(
      to right,
      transparent,
      hsla(0deg, 0%, 100%, 0.3),
      transparent
    );
    animation: shimmer 1.5s infinite;
  }
}

.squads {
  display: flex;
  flex-direction: column;
  gap: 1.2em;
}

.squad {
  display: flex;
  align-items: center;
  background-color: color-get(--root-rgb-bg-card);
  border-radius: 1.2em;
  padding: 1.2em;
  gap: 1.2em;
  cursor: wait;
}

.info {
  display: flex;
  align-items: center;
  gap: 0.8em;
}

.avatar {
  width: 5em;
  height: 5em;
  border-radius: 0.6em;
  @extend .skeletonItem;
}

.desc {
  display: flex;
  flex-direction: column;
  gap: 0.6em;
}

.head {
  display: flex;
  align-items: center;
  gap: 0.8em;
}

.tag {
  width: 5em;
  height: 1.2em;
  border-radius: 0.4em;
  @extend .skeletonItem;
}

.points {
  width: 4em;
  height: 1.2em;
  border-radius: 0.4em;
  @extend .skeletonItem;
}

.name {
  width: 12em;
  height: 1.6em;
  border-radius: 0.6em;
  @extend .skeletonItem;
}

.meta {
  width: 16em;
  height: 1.2em;
  border-radius: 0.4em;
  @extend .skeletonItem;
}

.members {
  display: flex;
  align-items: center;
  gap: 0.4em;
  margin-left: auto;
}

.memberAvatar {
  width: 3em;
  height: 3em;
  border-radius: 50%;
  border: 1px solid color-get(--root-rgb-bg-border);
  @extend .skeletonItem;
}

.slots {
  width: 3.6em;
  height: 2.4em;
  border-radius: 1.2em;
  @extend .skeletonItem;
}

.cover {
  width: 20em;
  height: 5em;
  border-radius: 1.2em;
  margin-left: 1.2em;
  @extend .skeletonItem;
}
</style>
